import { useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { forwardRef, useEffect, useRef } from 'react';
import { disabledNativeFieldClassNames } from '../utils/fieldStyleClasses';
import type { TextAreaProps } from './TextArea.types';

const baseClassNames = `
  tw:w-full
  tw:rounded-md
  tw:border
  tw:border-solid
  tw:p-1
  tw:px-3
  tw:py-1
  tw:text-sm
  tw:leading-normal
  tw:outline-none
`;

const darkThemeClassNames =
  'tw:dark:bg-sq-dark-background tw:dark:text-sq-dark-text tw:dark:placeholder-sq-dark-text/30!';
const lightThemeClassNames = 'tw:text-sq-text-color tw:placeholder-gray-400!';

const errorClassNames = 'tw:border-sq-danger';

const borderColorClassNames = `
  tw:border-sq-disabled-gray
  tw:hover:border-sq-theme-dark
  tw:focus:border-sq-theme-dark
  tw:active:border-sq-theme-dark
  tw:dark:border-sq-dark-disabled-gray
  tw:dark:hover:border-sq-theme-darker
  tw:dark:focus:border-sq-theme-darker
  tw:dark:active:border-sq-theme-darker
`;

/**
 * TextArea.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      readonly = false,
      disabled = false,
      onChange,
      onKeyUp,
      onFocus,
      onBlur,
      onKeyDown,
      onSelectionChange,
      id,
      name,
      rows = 3,
      cols = undefined,
      value,
      placeholder,
      showError,
      extraClassNames = '',
      testId,
      autoFocus = false,
    },
    ref,
  ) => {
    const appliedClassNames = clsx(
      baseClassNames,
      disabledNativeFieldClassNames,
      extraClassNames,
      lightThemeClassNames,
      darkThemeClassNames,
      showError ? errorClassNames : borderColorClassNames,
    );
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const mergedRef = useMergedRef(ref, textareaRef);

    // Handle selection change events
    useEffect(() => {
      if (!onSelectionChange || !textareaRef.current) {
        return;
      }

      const textarea = textareaRef.current;

      const handleSelectionChange = (e: Event) => {
        onSelectionChange(e);
      };

      // Add event listeners for selection changes
      textarea.addEventListener('selectionchange', handleSelectionChange);

      // Also listen for mouse and keyboard events that change selection
      textarea.addEventListener('mouseup', handleSelectionChange);
      textarea.addEventListener('keyup', handleSelectionChange);

      return () => {
        textarea.removeEventListener('selectionchange', handleSelectionChange);
        textarea.removeEventListener('mouseup', handleSelectionChange);
        textarea.removeEventListener('keyup', handleSelectionChange);
      };
    }, [onSelectionChange]);

    return (
      <textarea
        ref={mergedRef}
        data-testid={testId}
        name={name}
        id={id}
        value={value}
        className={appliedClassNames}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readonly}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        rows={rows}
        cols={cols}
        autoFocus={autoFocus}
      />
    );
  },
);
TextArea.displayName = 'TextArea';
